import styled, { css } from 'styled-components';

import { ScrollArea as BaseScrollArea } from '@mantine/core';
import { Combobox } from '@mantine/core';
import { themeColor } from '../theme/colors';
import { smBold, xxsBold } from '../theme/typography';
import { clickableMixin, ellipsisMixin } from '../theme/utils';
import { Search } from '../Search/Search';
import { HudIcon } from '../Icons/HudIcons';

const StyledSearch = styled(Search)`
  border: 1px solid ${themeColor('strokePrimary')};
`;

const Dropdown = styled(Combobox.Dropdown)<{ $fullWidth?: boolean }>`
  position: absolute;
  display: flex;
  min-width: 180px;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;

  border-radius: 8px;
  background: ${themeColor('buttonBackground')};
  box-shadow: 0px 3px 4px 0px rgba(0, 0, 0, 0.04);

  ${(props) =>
    !props.$fullWidth &&
    css`
      width: fit-content !important; // important because Mantine set width in style attribute
      max-width: 400px;
    `}

  &:has(${StyledSearch}) {
    padding-top: 16px;
  }
`;

const Options = styled(Combobox.Options)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  gap: 0;
  width: 100%;
  background: ${themeColor('buttonBackground')};
`;

const ScrollArea = styled(BaseScrollArea.Autosize).attrs({
  type: 'auto',
  offsetScrollbars: true,
  scrollbars: 'y',
})`
  width: 100%;
  max-height: 200px;
  --scrollarea-scrollbar-size: 12px;

  & .mantine-ScrollArea-scrollbar {
    background: unset;
  }

  & .mantine-ScrollArea-viewport {
    &:has(+ [data-state='hidden']) {
      padding-inline-end: unset;
    }
  }

  & .mantine-ScrollArea-thumb,
  &:hover .mantine-ScrollArea-thumb {
    background-color: ${themeColor('strokePrimary')};
    border-radius: 4px;
  }
`;

const CheckedContainer = styled.span`
  width: 17px;
  height: 17px;
  border-radius: 4px;
  border: 1px solid ${themeColor('strokePrimary')};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const OptionLabel = styled.div`
  ${ellipsisMixin}
  max-width: 300px;
`;
const Option = styled(Combobox.Option)`
  ${clickableMixin}
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  ${smBold};
  color: ${themeColor('textPrimary')};

  border-radius: 8px;
  /* on hover change to blue */
  &:hover {
    background: ${themeColor('buttonBackgroundHover')};
  }

  ${(props) =>
    props.disabled &&
    css`
      color: ${themeColor('textTertiary')};
      cursor: default;
      &:hover {
        background: ${themeColor('transparent')};
      }
    `}

  ${(props) =>
    props.selected &&
    css`
      & ${CheckedContainer} {
        border: 1px solid ${themeColor('textPrimary')};
      }
    `}
`;

const Divider = styled.div`
  border-bottom: 1px solid ${themeColor('strokeSecondary')};
  margin: 4px 0;
`;

const Group = styled.div``;

const GroupCaret = styled.div<{ expanded?: boolean }>`
  color: ${themeColor('gray')};

  transition: transform 0.2s;

  transform: rotate(-90deg);

  ${(props) =>
    props.expanded &&
    css`
      transform: rotate(0);
    `}
`;

const GroupTitle = styled.div`
  ${clickableMixin};
  ${xxsBold};
  color: ${themeColor('textPrimary')};
  margin: 0px 0 6px 0;

  display: flex;
  gap: 6px;
  align-items: center;
`;

const GroupOptionsContainer = styled.div`
  ${smBold};
  color: ${themeColor('textPrimary')};
  margin: 8px 0;

  padding-left: 2rem;

  ${Option} {
    ${xxsBold};
    padding: 4px;
    gap: 6px;
  }
`;

const SelectIcon = styled(HudIcon).attrs({
  size: '18px',
})`
  color: ${themeColor('gray')};
`;

export default {
  Search: StyledSearch,
  Dropdown,
  Options,
  Option,
  OptionLabel,
  CheckedContainer,
  ScrollArea,
  Divider,
  Group,
  GroupCaret,
  GroupTitle,
  GroupOptionsContainer,
  SelectIcon,
};
